import { area, curveMonotoneX, line } from 'd3-shape';
import { scaleLinear } from 'd3-scale';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';

interface EquityCurveProps {
  data: number[];
  width?: number;
  height?: number;
  stroke?: string;
}

export function EquityCurve({ data, width = 680, height = 280, stroke }: EquityCurveProps) {
  const theme = useTheme();
  const color = stroke ?? theme.color.accent.amber;
  const id = useMemo(
    () => `eq-fill-${Math.random().toString(36).slice(2, 8)}`,
    [],
  );

  const { linePath, areaPath } = useMemo(() => {
    if (data.length === 0) return { linePath: '', areaPath: '' };
    const min = Math.min(...data);
    const max = Math.max(...data);
    const x = scaleLinear().domain([0, data.length - 1]).range([0, width]);
    const y = scaleLinear().domain([min, max]).range([height - 4, 4]);
    const ln = line<number>()
      .x((_, i) => x(i))
      .y((d) => y(d))
      .curve(curveMonotoneX);
    const ar = area<number>()
      .x((_, i) => x(i))
      .y0(height)
      .y1((d) => y(d))
      .curve(curveMonotoneX);
    return { linePath: ln(data) ?? '', areaPath: ar(data) ?? '' };
  }, [data, width, height]);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={height}
      role="img"
      aria-label="Equity curve"
    >
      <defs>
        <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor={color} stopOpacity="0.28" />
          <stop offset="1" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${id})`} />
      <path d={linePath} fill="none" stroke={color} strokeWidth={1.4} />
    </svg>
  );
}
